import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, PictureOutlined } from '@ant-design/icons';
import { Card, Col, Form, Input, Progress, Row, Space, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';

import { metaDescriptionRange, seoTitleRange } from './seoChecks';
import type { SeoCheck, SeoReport, SeoStatus } from './seoChecks';
import { BrandColors } from '../../theme/brand';

const statusColors: Readonly<Record<SeoStatus, string>> = {
  good: BrandColors.green,
  warning: BrandColors.gold,
  bad: BrandColors.red,
};

const statusOrder: Readonly<Record<SeoStatus, number>> = { bad: 0, warning: 1, good: 2 };

function statusIcon(status: SeoStatus): ReactNode {
  const style = { color: statusColors[status], fontSize: 15, marginTop: 3 };

  if (status === 'good') {
    return <CheckCircleFilled style={style} />;
  }

  return status === 'warning' ? <ExclamationCircleFilled style={style} /> : <CloseCircleFilled style={style} />;
}

function scoreColor(score: number): string {
  if (score >= 80) {
    return statusColors.good;
  }

  return score >= 50 ? statusColors.warning : statusColors.bad;
}

function scoreLabel(score: number): string {
  if (score >= 80) {
    return 'Tốt — bài sẵn sàng cho Google';
  }

  return score >= 50 ? 'Khá — nên sửa thêm vài điểm' : 'Cần cải thiện';
}

/** Thanh đo độ dài, đổi màu theo khoảng Google hiển thị trọn. */
function LengthMeter(props: {
  readonly value: string;
  readonly range: { readonly min: number; readonly max: number };
  readonly fallbackNote: string | null;
}): ReactNode {
  const length = props.value.trim().length;
  const status: SeoStatus = length === 0 ? 'bad' : length < props.range.min || length > props.range.max ? 'warning' : 'good';

  return (
    <div>
      <Progress
        percent={Math.min(100, (length / props.range.max) * 100)}
        showInfo={false}
        size="small"
        strokeColor={statusColors[status]}
        style={{ margin: 0 }}
      />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {length.toString()}/{props.range.max.toString()} ký tự — nên {props.range.min.toString()}–{props.range.max.toString()}
        {props.fallbackNote === null ? '' : ` · ${props.fallbackNote}`}
      </Typography.Text>
    </div>
  );
}

/** Kết quả trên trang tìm kiếm Google — đúng cỡ chữ, màu và cách cắt của Google. */
function GoogleSnippet(props: { readonly title: string; readonly description: string; readonly slug: string }): ReactNode {
  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: '14px 16px',
        background: '#fff',
        fontFamily: 'Arial, Helvetica, sans-serif',
        maxWidth: 620,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#e8f0fe',
            color: '#1a56db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          M
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, color: '#202124', lineHeight: 1.3 }}>MVA Lab</div>
          <div style={{ fontSize: 12, color: '#4d5156', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            https://mvalab.vn › blog › {props.slug === '' ? '…' : props.slug}
          </div>
        </div>
      </div>
      <div
        style={{
          fontSize: 20,
          color: '#1a0dab',
          lineHeight: 1.3,
          marginTop: 8,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {props.title === '' ? 'Tiêu đề bài viết' : props.title}
      </div>
      <div
        style={{
          fontSize: 14,
          color: '#4d5156',
          lineHeight: 1.58,
          marginTop: 2,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {props.description === '' ? 'Chưa có mô tả — Google sẽ tự lấy một đoạn bất kỳ trong bài.' : props.description}
      </div>
    </div>
  );
}

/** Thẻ liên kết khi dán link bài lên Facebook hoặc Zalo. */
function SocialCard(props: { readonly title: string; readonly description: string; readonly imageUrl: string | null }): ReactNode {
  return (
    <div style={{ border: '1px solid #dadde1', borderRadius: 8, overflow: 'hidden', maxWidth: 520, background: '#fff' }}>
      {props.imageUrl === null ? (
        <div
          style={{
            aspectRatio: '1.91 / 1',
            background: '#f0f2f5',
            color: '#bfbfbf',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          <PictureOutlined style={{ fontSize: 28 }} />
          <span style={{ fontSize: 12 }}>Chưa có ảnh bìa</span>
        </div>
      ) : (
        <img
          src={props.imageUrl}
          alt=""
          style={{ width: '100%', aspectRatio: '1.91 / 1', objectFit: 'cover', display: 'block' }}
        />
      )}
      <div style={{ background: '#f0f2f5', padding: '10px 12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <div style={{ fontSize: 12, color: '#606770', textTransform: 'uppercase' }}>mvalab.vn</div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#1d2129',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {props.title === '' ? 'Tiêu đề bài viết' : props.title}
        </div>
        <div style={{ fontSize: 14, color: '#606770', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {props.description}
        </div>
      </div>
    </div>
  );
}

function CheckRow({ check }: { readonly check: SeoCheck }): ReactNode {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
      {statusIcon(check.status)}
      <div style={{ minWidth: 0 }}>
        <Typography.Text style={{ fontSize: 13 }}>{check.title}</Typography.Text>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {check.detail}
          </Typography.Text>
        </div>
      </div>
    </div>
  );
}

interface SeoPanelProps {
  readonly report: SeoReport;
  /** Tiêu đề thực sự lên Google: tiêu đề SEO, hoặc tiêu đề bài khi bỏ trống. */
  readonly effectiveTitle: string;
  readonly titleIsFallback: boolean;
  /** Mô tả thực sự lên Google: mô tả SEO, hoặc tóm tắt khi bỏ trống. */
  readonly effectiveDescription: string;
  readonly descriptionIsFallback: boolean;
  readonly slug: string;
  readonly imageUrl: string | null;
}

/**
 * Khung "Tối ưu SEO" của trang soạn bài: ba trường SEO, bản xem trước trên Google và khi chia sẻ, cùng điểm
 * SEO kèm từng tiêu chí.
 *
 * Phải nằm trong biểu mẫu soạn bài: ba trường ở đây là trường của biểu mẫu đó.
 */
export function SeoPanel({
  report,
  effectiveTitle,
  titleIsFallback,
  effectiveDescription,
  descriptionIsFallback,
  slug,
  imageUrl,
}: SeoPanelProps): ReactNode {
  const sorted = [...report.checks].sort((left, right) => statusOrder[left.status] - statusOrder[right.status]);
  const count = (status: SeoStatus): number => report.checks.filter((check) => check.status === status).length;

  return (
    <Card
      title={
        <Space>
          Tối ưu SEO
          <Tag bordered={false} color={report.score >= 80 ? 'success' : report.score >= 50 ? 'warning' : 'error'}>
            {report.score.toString()}/100
          </Tag>
        </Space>
      }
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} xl={14}>
          <Form.Item
            name="focusKeyword"
            label="Từ khóa chính"
            tooltip="Cụm từ khách hay gõ trên Google mà bạn muốn bài này xuất hiện. Chỉ dùng để chấm điểm, không hiện trên website."
          >
            <Input placeholder="Ví dụ: kiểm tra lỗi bằng camera" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="seoTitle"
            label="Tiêu đề SEO"
            tooltip="Dòng chữ xanh trên kết quả Google và tiêu đề khi chia sẻ. Để trống thì dùng tiêu đề bài."
            extra={
              <LengthMeter
                value={effectiveTitle}
                range={seoTitleRange}
                fallbackNote={titleIsFallback ? 'đang dùng tiêu đề bài' : null}
              />
            }
          >
            <Input placeholder="Để trống thì dùng tiêu đề bài" maxLength={120} />
          </Form.Item>

          <Form.Item
            name="metaDescription"
            label="Mô tả SEO"
            tooltip="Đoạn chữ xám dưới tiêu đề trên Google. Để trống thì dùng tóm tắt."
            extra={
              <LengthMeter
                value={effectiveDescription}
                range={metaDescriptionRange}
                fallbackNote={descriptionIsFallback ? 'đang dùng tóm tắt' : null}
              />
            }
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Để trống thì dùng tóm tắt" maxLength={320} />
          </Form.Item>

          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Xem trước trên Google
          </Typography.Text>
          <GoogleSnippet title={effectiveTitle} description={effectiveDescription} slug={slug} />

          <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>
            Khi chia sẻ lên Facebook, Zalo
          </Typography.Text>
          <SocialCard title={effectiveTitle} description={effectiveDescription} imageUrl={imageUrl} />
        </Col>

        <Col xs={24} xl={10}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
            <Progress
              type="circle"
              percent={report.score}
              size={84}
              strokeColor={scoreColor(report.score)}
              format={(percent) => (percent ?? 0).toString()}
            />
            <div>
              <Typography.Text strong style={{ fontSize: 15 }}>
                {scoreLabel(report.score)}
              </Typography.Text>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {count('good').toString()} đạt · {count('warning').toString()} nên sửa · {count('bad').toString()} thiếu
                </Typography.Text>
              </div>
            </div>
          </div>
          {sorted.map((check) => (
            <CheckRow key={check.key} check={check} />
          ))}
        </Col>
      </Row>
    </Card>
  );
}
